import React, { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, ShieldCheck } from "lucide-react";

const screens = [
  {
    id: "overview",
    title: "Operations Overview",
    caption: "Live command centre — contractors, contracts and payouts at a glance.",
    img: "/screenshots/01-dashboard.png",
    badge: { value: "4", label: "Active contractors", tone: "green" },
  },
  {
    id: "contracts",
    title: "Contract Management",
    caption: "Create, send and track contractor agreements end-to-end.",
    img: "/screenshots/02-contracts.png",
    badge: { value: "Active", label: "Contract signed", tone: "accent" },
  },
  {
    id: "esignature",
    title: "eSignature",
    caption: "Send contracts, collect signatures and track completion in one view.",
    img: "/screenshots/03-esignature.png",
    badge: { value: "86%", label: "Signature rate", tone: "green" },
  },
  {
    id: "projects",
    title: "Project Management",
    caption: "Budget tracking and resource allocation across every engagement.",
    img: "/screenshots/04-projects.png",
    badge: { value: "€68,200", label: "Budget tracked", tone: "accent" },
  },
  {
    id: "compliance",
    title: "Compliance Center",
    caption: "Automated working-time compliance monitoring — zero violations.",
    img: "/screenshots/05-compliance.png",
    badge: { value: "100%", label: "Compliance score", tone: "green" },
  },
];

function StatBadge({ badge, visible }) {
  const valueColor = badge.tone === "green" ? "text-[#34D399]" : "text-[#FF4F00]";
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="badge"
          initial={{ opacity: 0, y: 14, scale: 0.94 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.96 }}
          transition={{ duration: 0.45, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="absolute bottom-5 right-5 glass rounded-2xl px-4 py-3 border border-[#1F3535] shadow-[0_8px_40px_rgba(0,0,0,0.55)] pointer-events-none"
        >
          <div className={`font-display text-2xl font-bold leading-none tracking-tight ${valueColor}`}>
            {badge.value}
          </div>
          <div className="text-[11px] text-[#9BB8B8] mt-1 font-mono">{badge.label}</div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function Showcase() {
  const ref = useRef(null);
  const cardRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const goTo = (index) => setActiveIndex(index);

  useEffect(() => {
    const card = cardRefs.current[activeIndex];
    const container = ref.current;
    if (!card || !container) return;
    const containerRect = container.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const targetLeft = container.scrollLeft + (cardRect.left - containerRect.left);
    container.scrollTo({ left: targetLeft, behavior: "smooth" });
  }, [activeIndex]);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % screens.length);
    }, 3800);
    return () => clearInterval(timer);
  }, [isPaused]);

  return (
    <section id="showcase" data-testid="showcase-section" className="relative py-16 sm:py-24 lg:py-36 border-t border-[#0E1A1A]">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12 mb-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="text-[11px] uppercase tracking-[0.22em] text-[#FF4F00] font-bold mb-3"
            >
              Product tour
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.65, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.035em] leading-[1.02] font-medium"
            >
              The product, in motion.
            </motion.h2>
          </div>

          <div className="flex items-center gap-4">
            {/* Dot indicators */}
            <div className="flex items-center gap-1.5">
              {screens.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    activeIndex === i ? "bg-[#FF4F00] w-5" : "bg-[#1F3535] w-1.5 hover:bg-[#2A4444]"
                  }`}
                />
              ))}
            </div>
            {/* Arrow controls */}
            <div className="flex gap-2">
              <button
                onClick={() => goTo(((activeIndex - 1) + screens.length) % screens.length)}
                data-testid="showcase-prev"
                className="w-11 h-11 rounded-full border border-[#152222] hover:border-[#1F3535] hover:bg-[#0D1A1A] transition grid place-items-center"
                aria-label="Previous screen"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => goTo((activeIndex + 1) % screens.length)}
                data-testid="showcase-next"
                className="w-11 h-11 rounded-full border border-[#152222] hover:border-[#1F3535] hover:bg-[#0D1A1A] transition grid place-items-center"
                aria-label="Next screen"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        ref={ref}
        data-testid="showcase-carousel"
        className="flex gap-6 overflow-x-auto px-6 lg:px-12 pb-8 no-scrollbar snap-x snap-mandatory"
        style={{ scrollPaddingInline: "48px" }}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {screens.map((s, i) => (
          <motion.div
            key={s.id}
            ref={el => (cardRefs.current[i] = el)}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5, delay: i * 0.05 }}
            onClick={() => goTo(i)}
            className="snap-start shrink-0 w-[88vw] sm:w-[600px] lg:w-[720px] cursor-pointer"
            data-testid={`showcase-item-${s.id}`}
          >
            {/* Slide label row */}
            <div className="mb-3 flex items-baseline justify-between">
              <div className="font-display text-lg tracking-tight">{s.title}</div>
              <div className={`text-xs font-mono transition-colors duration-300 ${
                activeIndex === i ? "text-[#FF4F00]" : "text-[#7A9B9B]"
              }`}>
                0{i + 1} / 0{screens.length}
              </div>
            </div>

            {/* Browser chrome frame */}
            <div className="rounded-2xl overflow-hidden border border-[#1A2E2E] shadow-[0_32px_80px_rgba(0,0,0,0.65)]">
              <div className="flex items-center gap-2 px-4 py-2.5 bg-[#0D1A1A] border-b border-[#152222]">
                <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
                <div className="ml-3 px-3 py-1 rounded-md bg-[#0A1414] border border-[#152222] text-[10px] text-[#7A9B9B] font-mono flex items-center gap-1.5 max-w-[260px]">
                  <ShieldCheck className="w-3 h-3 text-[#34D399] shrink-0" />
                  app.nexus-vs.vawick.com
                </div>
              </div>

              {/* Screenshot + floating badge */}
              <div className="relative bg-[#F3F4F6]">
                <img
                  src={s.img}
                  alt={s.title}
                  className="w-full block"
                  loading="lazy"
                />
                <StatBadge badge={s.badge} visible={activeIndex === i} />
              </div>
            </div>

            {/* Caption */}
            <div className="mt-3 text-sm text-[#9BB8B8]">{s.caption}</div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
